import type { NotificationTemplateEvent } from '@fleet/shared';
import { apiFetch } from './client';

/** Eine Angabe in der Statuszeile. Der Server schickt hier nie ein Geheimnis. */
export interface Fact {
  label: string;
  value: string;
}

export interface IntegrationStatus {
  channel: 'SLACK' | 'EMAIL';
  isConfigured: boolean;
  /** Fehlende Konfigurationsschlüssel, beim Namen genannt. */
  missing: string[];
  facts: Fact[];
  /**
   * Was an der Einstellung erklärt werden muss — etwa dass unverschlüsselt
   * gesendet wird. Kein Fehler, sondern eine Folge, die sichtbar sein soll.
   */
  warnings: string[];
  /** Vorbelegung des Empfängerfelds — die angemeldete Person selbst. */
  defaultRecipient?: string;
}

/**
 * Ergebnis einer Prüfung oder eines Testversands.
 *
 * **Ein Fehlschlag kommt als 200 mit `ok: false`**, nicht als `ApiError`. Der
 * Grund steht am Endpunkt: „nein, weil der Bot nicht im Channel ist" ist die
 * Antwort auf die gestellte Frage und kein Fehler der Anfrage. Deshalb wird das
 * Ergebnis hier auch nicht an `ErrorNotice` gereicht — der Baustein verzweigt auf
 * `ApiErrorCode` und kennt Slacks Vokabular nicht.
 */
export interface IntegrationTestResult {
  ok: boolean;
  deliveredTo?: string;
  /** Slack-Fehlercode bzw. nodemailer-Code, zum Nachschlagen. */
  code?: string;
  message: string;
  facts?: Fact[];
}

/**
 * Integrationen prüfen — ausschließlich für die Administration.
 *
 * Der Empfänger ist überall optional: Ohne Angabe geht die Testnachricht an die
 * angemeldete Person selbst.
 */
export const integrationsApi = {
  status: () => apiFetch<IntegrationStatus[]>('/api/v1/admin/integrations'),

  /** Prüft nur den Zugang — es wird nichts zugestellt. */
  checkSlack: () =>
    apiFetch<IntegrationTestResult>('/api/v1/admin/integrations/slack/check', { method: 'POST' }),

  checkEmail: () =>
    apiFetch<IntegrationTestResult>('/api/v1/admin/integrations/email/check', { method: 'POST' }),

  testSlack: (recipient?: string) =>
    apiFetch<IntegrationTestResult>('/api/v1/admin/integrations/slack/test', {
      method: 'POST',
      body: JSON.stringify(recipient ? { recipient } : {}),
    }),

  testEmail: (recipient?: string) =>
    apiFetch<IntegrationTestResult>('/api/v1/admin/integrations/email/test', {
      method: 'POST',
      body: JSON.stringify(recipient ? { recipient } : {}),
    }),

  /** Die Texte der zeitgesteuerten Nachrichten — global, nicht je Fahrzeuggruppe. */
  templates: () =>
    apiFetch<NotificationTemplateView[]>('/api/v1/admin/integrations/templates'),

  setTemplate: (event: NotificationTemplateEvent, body: { isEnabled: boolean; body: string }) =>
    apiFetch<NotificationTemplateView>(`/api/v1/admin/integrations/templates/${event}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

export interface NotificationTemplateView {
  event: NotificationTemplateEvent;
  /** „Wenn eine Buchung beginnt" — kommt vom Server, damit es eine Quelle hat. */
  label: string;
  isEnabled: boolean;
  body: string;
  isDefault: boolean;
  updatedAt?: string;
}
